import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FONT, API_URL } from '../constants';
import { C } from '../styles';
import { authHeaders } from '../helpers';
import CatalogList from './CatalogList';
import RateOverrideInput from './RateOverrideInput';

type DrawerMode = 'earning' | 'deduction';

interface EditItemWrapper {
  item: any;
  kind: DrawerMode;
}

interface Props {
  mode: DrawerMode;
  employeeId: string | number | undefined;
  editItem: EditItemWrapper | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function CompensationDrawer({ mode, employeeId, editItem, onClose, onSaved }: Props) {
  const isEarning = mode === 'earning';
  const isEditing = Boolean(editItem);

  let editTypeId: string | number | null = null;
  let editOverride = '';
  let editItemName = '';
  if (isEditing && editItem) {
    const item = editItem.item;
    if (isEarning) {
      editTypeId = item.pay_type_id;
      editOverride = item.rate_override != null ? String(item.rate_override) : '';
      editItemName = (item.pay_type && item.pay_type.name) || '';
    } else {
      editTypeId = item.deduction_type_id;
      editOverride = item.amount_override != null ? String(item.amount_override) : '';
      editItemName = (item.deduction_type && item.deduction_type.name) || '';
    }
  }

  const title = isEditing
    ? (isEarning ? 'Edit rate for ' + editItemName : 'Edit amount for ' + editItemName)
    : (isEarning ? 'Add earning' : 'Add deduction');
  const sub = isEditing
    ? (isEarning
      ? 'Update the rate for this employee. Leave blank to use the catalog default.'
      : 'Update the amount for this employee. Leave blank to use the catalog default.')
    : (isEarning
      ? 'Pick a pay type from your catalog and set the rate'
      : 'Pick a deduction type from your catalog and set the amount');

  const [catalog, setCatalog] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedId, setSelectedId] = useState<string | number | null>(editTypeId);
  const [rateOverride, setRateOverride] = useState<string>(editOverride);
  const [saving, setSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    const headers = { 'Content-Type': 'application/json', ...authHeaders() };
    const endpoint = isEarning ? '/api/v1/pay-types' : '/api/v1/deduction-types';
    fetch(API_URL + endpoint, { headers })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        const items = Array.isArray(data) ? data : (data.items || []);
        const activeOnly = items.filter((t: any) => t.is_active !== false);
        setCatalog(activeOnly);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [isEarning]);

  const handleSelect = (id: string | number): void => {
    setSelectedId(id);
    setRateOverride('');
  };

  const handleSave = (): void => {
    if (!selectedId || saving) return;
    setSaving(true);
    setSaveError(null);

    const overrideField = isEarning ? 'rate_override' : 'amount_override';
    const typeIdField = isEarning ? 'pay_type_id' : 'deduction_type_id';
    const collectionEndpoint = isEarning ? '/api/v1/employee-pay-items' : '/api/v1/employee-deduction-items';
    const headers = { 'Content-Type': 'application/json', ...authHeaders() };
    const overrideValue = rateOverride.trim() === '' ? null : Number(rateOverride);

    let url: string;
    let fetchOpts: RequestInit;
    if (isEditing && editItem) {
      const patchBody: Record<string, unknown> = {};
      patchBody[overrideField] = overrideValue;
      url = API_URL + collectionEndpoint + '/' + editItem.item.id;
      fetchOpts = { method: 'PATCH', headers, body: JSON.stringify(patchBody) };
    } else {
      const body: Record<string, unknown> = { employee_id: employeeId, notes: null };
      body[typeIdField] = selectedId;
      body[overrideField] = overrideValue;
      url = API_URL + collectionEndpoint;
      fetchOpts = { method: 'POST', headers, body: JSON.stringify(body) };
    }

    fetch(url, fetchOpts)
      .then((r) => {
        if (!r.ok) return r.text().then((t) => { throw new Error(t || 'Failed to save'); });
        return r.json();
      })
      .then(() => {
        setSaving(false);
        if (onSaved) onSaved();
      })
      .catch((err) => {
        setSaving(false);
        setSaveError(err.message || 'Failed to save. Please try again.');
      });
  };

  const selectedItem = catalog.find((t) => t.id === selectedId) || null;

  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(14,26,31,0.35)',
        zIndex: 1000, display: 'flex', justifyContent: 'flex-end',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 520, height: '100vh', background: '#fff',
          boxShadow: '-20px 0 40px rgba(14,26,31,0.15)',
          display: 'flex', flexDirection: 'column', fontFamily: FONT,
        }}
      >
        <div style={{
          padding: '20px 26px 18px', borderBottom: '1px solid ' + C.line,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: C.ink, letterSpacing: '-0.01em', marginBottom: 2 }}>{title}</div>
            <div style={{ fontSize: 12.5, color: (C as any).muted }}>{sub}</div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 0, color: (C as any).faint,
              cursor: 'pointer', padding: 6, borderRadius: 6,
              display: 'grid', placeItems: 'center',
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '22px 26px' }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: (C as any).muted, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 11 }}>
            {isEarning ? 'Pay type' : 'Deduction type'}
          </div>

          <CatalogList
            catalog={catalog}
            loading={loading}
            isEarning={isEarning}
            selectedId={selectedId}
            isEditing={isEditing}
            editTypeId={editTypeId}
            onSelect={handleSelect}
          />

          {selectedItem && (
            <RateOverrideInput
              isEarning={isEarning}
              selectedItem={selectedItem}
              rateOverride={rateOverride}
              setRateOverride={setRateOverride}
            />
          )}

          {saveError && (
            <div style={{
              marginTop: 16, padding: '11px 14px',
              background: '#FEF2F2', border: '1px solid #FCA5A5',
              borderRadius: 8, color: '#B53B2E',
              fontSize: 12.5, lineHeight: 1.5,
            }}>
              {saveError}
            </div>
          )}
        </div>

        <div style={{
          padding: '18px 26px', borderTop: '1px solid ' + C.line,
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
          gap: 10, background: '#fff',
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 18px', borderRadius: 10,
              fontFamily: FONT, fontWeight: 600, fontSize: 14,
              cursor: 'pointer',
              border: '1px solid ' + C.line, color: C.ink, background: '#fff',
            }}
          >
            Cancel
          </button>
          <button
            disabled={!selectedId || saving}
            onClick={handleSave}
            style={{
              padding: '10px 18px', borderRadius: 10,
              fontFamily: FONT, fontWeight: 600, fontSize: 14,
              cursor: (selectedId && !saving) ? 'pointer' : 'not-allowed',
              border: '1px solid transparent', color: '#fff',
              background: (selectedId && !saving) ? '#0E1A1A' : '#C3CBD6',
              boxShadow: (selectedId && !saving) ? '0 1px 2px rgba(14,26,31,0.15)' : 'none',
            }}
          >
            {saving ? (isEditing ? 'Updating...' : 'Adding...') : (isEditing ? 'Update' : 'Add to employee')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}